import { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import type { Locale } from "@/i18n";

// ─── One-off shop closure announcement ─────────────────────────────────────
// Shop is closed on July 17–18, 2026 (JST). Banner auto-hides after that
// date passes — safe to delete after the event.
const CLOSURE_LAST_DAY = { year: 2026, month: 7, day: 18 } as const;
const STORAGE_KEY = "kra-guide-closure-2026-07-17-dismissed";

type Copy = { title: string; body: string; dismiss: string };

const COPY: Record<Locale, Copy> = {
  en: {
    title: "Shop closed July 17–18, 2026",
    body: "Our team will be away on these two days. Please avoid these dates when planning your visit.",
    dismiss: "Dismiss",
  },
  vi: {
    title: "Shop nghỉ ngày 17–18/7/2026",
    body: "Cả team sẽ đi vắng trong 2 ngày này. Anh chị vui lòng tránh đặt lịch vào hai ngày này.",
    dismiss: "Đóng",
  },
  ja: {
    title: "休業のお知らせ:2026年7月17日〜18日",
    body: "この2日間は休業させていただきます。ご来店のご予定は別日をご検討ください。",
    dismiss: "閉じる",
  },
  zh: {
    title: "店铺休业通知:2026年7月17–18日",
    body: "此两日本店休业,请勿在此日期内预约。",
    dismiss: "关闭",
  },
  th: {
    title: "ร้านปิดทำการ 17–18 ก.ค. 2026",
    body: "ทีมงานของเราจะหยุดในสองวันนี้ กรุณาหลีกเลี่ยงการจองในช่วงเวลานี้",
    dismiss: "ปิด",
  },
  id: {
    title: "Toko tutup 17–18 Juli 2026",
    body: "Tim kami akan libur pada dua hari ini. Mohon hindari tanggal tersebut saat merencanakan kunjungan.",
    dismiss: "Tutup",
  },
  es: {
    title: "Tienda cerrada el 17–18 de julio de 2026",
    body: "Nuestro equipo estará ausente esos dos días. Por favor, evite estas fechas al planificar su visita.",
    dismiss: "Cerrar",
  },
};

function isStillRelevant(): boolean {
  const now = new Date();
  const jstMs = now.getTime() + now.getTimezoneOffset() * 60_000 + 9 * 60 * 60_000;
  const jst = new Date(jstMs);
  const year = jst.getUTCFullYear();
  const month = jst.getUTCMonth() + 1;
  const day = jst.getUTCDate();
  if (year !== CLOSURE_LAST_DAY.year) return year < CLOSURE_LAST_DAY.year;
  if (month !== CLOSURE_LAST_DAY.month) return month < CLOSURE_LAST_DAY.month;
  return day <= CLOSURE_LAST_DAY.day;
}

export function ClosureBanner() {
  const { locale } = useI18n();
  const [visible, setVisible] = useState<boolean>(() => {
    if (!isStillRelevant()) return false;
    try {
      return window.localStorage.getItem(STORAGE_KEY) !== "1";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!isStillRelevant()) setVisible(false);
  }, [locale]);

  if (!visible) return null;

  const copy = COPY[locale] ?? COPY.en;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
    setVisible(false);
  };

  return (
    <div
      className="bg-gradient-to-b from-[#fbf3e8] to-[#f6ead9] border-b border-[rgba(89,67,48,0.12)]"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 max-w-3xl mx-auto px-5 py-3">
        <span className="text-base leading-tight mt-0.5" aria-hidden="true">
          📅
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#3a2a1f] leading-snug">
            {copy.title}
          </p>
          <p className="text-xs text-[#6b5240] leading-snug mt-0.5">
            {copy.body}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={copy.dismiss}
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[#6b5240] hover:bg-black/5 hover:text-[#3a2a1f] transition-colors text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
}
